import React from "react";
import "./styles/index.css";
import "react-toastify/ReactToastify.css";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "./app/auth/contexts/providers/AuthContext.tsx";
import { ThemeProvider } from "./app/common/themes/context/ThemeContext.tsx";
import ErrorBoundary from "./app/common/components/errors/ErrorBoundary.tsx";
import { sessionManager } from "./app/auth/services/sessionManager.ts";
import { AppContent } from "./app/AppContent.tsx";

// Note: PageLoadingSpinner is no longer used since we removed the global Suspense boundary
// from RouterProvider to prevent full-page-like reloads. Lazy components now load
// gracefully without showing a full-screen spinner.

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1 * 60 * 1000,
      gcTime: 5 * 60 * 1000,
      retry: (failureCount, error) => {
        // Check if error is a 401/403 (session expired)
        const statusCode = extractStatusCode(error);
        if (statusCode === 401 || statusCode === 403) {
          console.warn('🔐 QueryClient: Detected session expiration (401/403), triggering logout');
          sessionManager.notifySessionExpired();
          return false; // Don't retry on auth errors
        }

        // Default retry logic
        if (error instanceof Error && error.message.includes("401")) {
          return false;
        }
        return failureCount < 3;
      },
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 1,
    },
  },
});

/**
 * Extract HTTP status code from error message or object
 * @param error - The error to extract status from
 * @returns Status code or undefined
 */
const extractStatusCode = (error: unknown): number | undefined => {
  if (typeof error === "object" && error !== null) {
    if ("status" in error && typeof error.status === "number") {
      return error.status;
    }
    if ("statusCode" in error && typeof error.statusCode === "number") {
      return error.statusCode;
    }
    if ("response" in error && typeof error.response === "object") {
      const response = error.response as Record<string, unknown>;
      if ("status" in response && typeof response.status === "number") {
        return response.status;
      }
    }
  }

  // Try to extract from message
  const message = String(error);
  const statusMatch = message.match(/status[:\s]+(\d+)/i);
  return statusMatch ? parseInt(statusMatch[1], 10) : undefined;
};

const root = document.getElementById("root") as HTMLElement;

createRoot(root).render(
  <React.StrictMode>
    <ErrorBoundary>
      <ThemeProvider defaultTheme="system" storageKey="clinicmaster-ui-theme">
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
